// @givmo/app-charity — My claims list (CPA-WEB2, plan A-1).
//
// The caller's own claim requests + their translated status. Wiring:
//   - LIST → `charityAdminListMyCharityClaims` (GET /charity-admin/claims) via the
//     `charityAdminListMyCharityClaimsOptions` factory. Self-scoped — a caller only ever sees their
//     own claims. The response is the `{requests:[…]}` named wrapper; `extractClaims` decodes it
//     tolerantly. Newest first (backend order).
//
// Each row links to the claim-detail page (status + Tier-2 upload). A zero state invites the user to
// start a claim; an error state is honest and retryable. The root keeps `data-testid="page-claims"`.

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { charityAdminListMyCharityClaimsOptions } from '@givmo/api-client/query';
import { isGivmoError, type CharityClaimRequestOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { Building2, ChevronRight, FileText, Info, Plus } from 'lucide-react';
import { extractClaims, formatClaimDate, formatEin } from './claim-decode';
import { claimStatusLabel, claimStatusTone, isPendingStatus } from './claim-vocab';
import { CLAIM_ROUTES, claimDetailPath } from './claim-routes';

const PREFIX = 'claims';

export function ClaimsListPage() {
  const navigate = useNavigate();

  const claimsQuery = useQuery({
    ...charityAdminListMyCharityClaimsOptions(),
    select: (data: unknown) => extractClaims(data),
    refetchOnWindowFocus: 'always',
  });
  const claims: CharityClaimRequestOut[] = claimsQuery.data ?? [];
  const hasPendingClaim = claims.some((claim) => isPendingStatus(claim.status));

  return (
    <div
      className="mx-auto flex w-full max-w-[900px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-claims')}
    >
      <PageHeader
        title="My claims"
        subtitle="Organization claims you've submitted and their status."
        actions={
          <Button
            appearance="saas"
            tone="primary"
            leadingIcon={<Icon icon={Plus} size={16} aria-hidden />}
            onClick={() => void navigate({ to: CLAIM_ROUTES.search })}
            testId={`${PREFIX}-new`}
          >
            Claim an organization
          </Button>
        }
      />

      {claimsQuery.isSuccess && hasPendingClaim ? (
        <Card className="border-brand-100 bg-brand-50 p-16" testId={`${PREFIX}-under-review`}>
          <div className="flex items-start gap-10">
            <Icon icon={Info} size={18} className="mt-1 shrink-0 text-brand-600" aria-hidden />
            <div>
              <p className="font-w600 text-text-strong">Your claim is under review</p>
              <p className="mt-2 text-s13 text-text-secondary">
                We&rsquo;ll email you when there&rsquo;s an update. You can return here to check its
                status.
              </p>
            </div>
          </div>
        </Card>
      ) : null}

      {claimsQuery.isPending && (
        <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-loading`)}>
          {[0, 1].map((i) => (
            <Card key={i} className="p-16">
              <div className="flex items-center justify-between gap-12">
                <div className="flex flex-1 flex-col gap-6">
                  <Skeleton className="h-14 w-1/3" />
                  <Skeleton className="h-12 w-1/4" />
                </div>
                <Skeleton className="h-20 w-20 rounded-md" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {claimsQuery.isError && (
        <EmptyState
          testId={`${PREFIX}-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn't load your claims"
          description={
            isGivmoError(claimsQuery.error)
              ? claimsQuery.error.message
              : 'Something went wrong loading your claims. Please try again.'
          }
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void claimsQuery.refetch()}
              testId={`${PREFIX}-error-retry`}
            >
              Try again
            </Button>
          }
        />
      )}

      {claimsQuery.isSuccess && claims.length === 0 && (
        <EmptyState
          testId={`${PREFIX}-empty`}
          icon={<Icon icon={FileText} size={20} aria-hidden />}
          title="No claims yet"
          description="You haven't claimed an organization yet. Search the catalog to claim your nonprofit and start managing it on Givmo."
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
              onClick={() => void navigate({ to: CLAIM_ROUTES.search })}
              testId={`${PREFIX}-empty-cta`}
            >
              Claim an organization
            </Button>
          }
        />
      )}

      {claimsQuery.isSuccess && claims.length > 0 && (
        <ul className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-list`)}>
          {claims.map((claim) => {
            const ein = formatEin(claim.attested_ein);
            const submitted = formatClaimDate(claim.created_at);
            const updated = formatClaimDate(claim.updated_at);
            return (
              <li key={claim.id}>
                <button
                  type="button"
                  onClick={() => void navigate({ to: claimDetailPath(claim.id) })}
                  className="w-full rounded-xl border border-border-subtle bg-surface p-16 text-left shadow-xs outline-none transition-colors hover:border-border-strong focus-visible:shadow-ring"
                  {...testIdProp(`${PREFIX}-row-${claim.id}`)}
                >
                  <div className="flex items-center justify-between gap-12">
                    <div className="min-w-0">
                      <div className="truncate font-w600 text-text-strong">
                        {claim.attested_legal_name || 'Organization claim'}
                      </div>
                      {ein ? (
                        <div className="mt-1 text-s13 text-text-tertiary">EIN {ein}</div>
                      ) : null}
                      {submitted || updated ? (
                        <div
                          className="mt-1 flex flex-wrap gap-x-8 text-s12 text-text-tertiary"
                          {...testIdProp(`${PREFIX}-row-${claim.id}-dates`)}
                        >
                          {submitted ? <span>Submitted {submitted}</span> : null}
                          {updated ? <span>Updated {updated}</span> : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-10">
                      <span {...testIdProp(`${PREFIX}-row-${claim.id}-status`)}>
                        <Badge tone={claimStatusTone(claim.status)} dot>
                          {claimStatusLabel(claim.status)}
                        </Badge>
                      </span>
                      <Icon icon={ChevronRight} size={16} className="text-icon-muted" aria-hidden />
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
